import React, { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { get } from '../../api/http';
import EventListCard from '../../components/EventListCard';
import {
    FREE_EVENTS_URL,
    TRENDING_EVENTS_URL,
    WEEKEND_EVENTS_URL
} from '../../constants/apiUrls';
import { Event } from '../../types/event';
import { filterActiveEvents } from '../../utils/eventUtils';
import './EventCategory.css';

const EventCategory: React.FC = () => {
    const { category } = useParams<{ category: string }>();
    const location = useLocation();
    const [events, setEvents] = useState<Event[]>([]);
    const [loading, setLoading] = useState(true);
    const [categoryTitle, setCategoryTitle] = useState('');

    useEffect(() => {
        const fetchEvents = async () => {
            setLoading(true);

            // Check if state is passed from navigation (prevents new API call)
            const state = location.state as { events?: Event[]; title?: string };
            if (state?.events) {
                setEvents(filterActiveEvents(state.events));
                setCategoryTitle(state.title || (category ? category.charAt(0).toUpperCase() + category.slice(1).replace('-', ' ') : 'Events'));
                setLoading(false);
                return;
            }

            let url = '';
            let title = '';

            switch (category) {
                case 'trending':
                    url = TRENDING_EVENTS_URL;
                    title = 'Trending';
                    break;
                case 'weekend':
                    url = WEEKEND_EVENTS_URL;
                    title = 'Weekend';
                    break;
                case 'free':
                    url = FREE_EVENTS_URL;
                    title = 'Free';
                    break;
                default:
                    // Fallback or handle other categories if API supports them
                    // For now, maybe redirect to 404 or show empty
                    title = category ? category.charAt(0).toUpperCase() + category.slice(1) : 'Events';
                    break;
            }

            setCategoryTitle(title);

            if (url) {
                try {
                    const response = await get<Event[]>(url);
                    setEvents(filterActiveEvents(response.data));
                } catch (error) {
                    console.error(`Failed to fetch ${category} events`, error);
                }
            }
            setLoading(false);
        };

        fetchEvents();
    }, [category, location.state]);

    useEffect(() => {
        const observerOptions = {
            threshold: 0.1,
            rootMargin: '0px 0px -50px 0px'
        };

        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry, index) => {
                if (entry.isIntersecting) {
                    (entry.target as HTMLElement).style.opacity = '1';
                    (entry.target as HTMLElement).style.transform = 'translateY(0)';
                    (entry.target as HTMLElement).style.transitionDelay = `${index * 0.05}s`;
                }
            });
        }, observerOptions);

        const cards = document.querySelectorAll('.event-card');
        cards.forEach((card) => {
            (card as HTMLElement).style.opacity = '0';
            (card as HTMLElement).style.transform = 'translateY(30px)';
            (card as HTMLElement).style.transition = 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)';
            observer.observe(card);
        });

        return () => observer.disconnect();
    }, [events, loading]);

    return (
        <div className="event-category-page">
            <div className="container">
                {/* Page Header */}
                <div className="page-header">
                    <h1 className="page-title">{categoryTitle} Events</h1>
                    <p className="page-subtitle">
                        {category === 'trending' && "Discover the hottest events everyone's talking about"}
                        {category === 'weekend' && "Amazing events happening this weekend"}
                        {category === 'free' && "Great events that won't cost you a penny"}
                        {category === 'music' && "Live music, concerts, and festivals"}
                        {category === 'business' && "Professional networking and business events"}
                        {!['trending', 'weekend', 'free', 'music', 'business'].includes(category || '') && "Discover amazing events in this category"}
                    </p>
                </div>

                {/* Results Info */}
                <div className="results-info">
                    <div className="results-count">
                        Showing {events.length} event{events.length !== 1 ? 's' : ''}
                    </div>
                    <Link to="/events" className="back-link">
                        <i className="fas fa-arrow-left"></i>
                        Back to Events
                    </Link>
                </div>

                {/* Events Grid */}
                {loading ? (
                    <div className="events-grid">
                        {Array.from({ length: 6 }).map((_, i) => (
                            <div className="event-card skeleton" key={i} style={{ height: '400px' }}>
                                {/* Skeleton content could go here */}
                            </div>
                        ))}
                    </div>
                ) : events.length > 0 ? (
                    <div className="events-grid">
                        {events.map(event => (
                            <EventListCard key={event.event_uuid} event={event} />
                        ))}
                    </div>
                ) : (
                    /* Empty State */
                    <div className="empty-state">
                        <div className="empty-state-icon">
                            {category === 'free' && <i className="fas fa-gift"></i>}
                            {category === 'music' && <i className="fas fa-music"></i>}
                            {category === 'business' && <i className="fas fa-briefcase"></i>}
                            {category === 'weekend' && <i className="fas fa-calendar-week"></i>}
                            {!['free', 'music', 'business', 'weekend'].includes(category || '') && <i className="fas fa-calendar-times"></i>}
                        </div>
                        <h3 className="empty-state-title">No {categoryTitle} Events Found</h3>
                        <p className="empty-state-text">
                            {category === 'trending' && "No trending events available right now. Check back later or explore other categories!"}
                            {category === 'weekend' && "No events scheduled for this weekend. Check back later for amazing events!"}
                            {category === 'free' && "No free events available at the moment. Check back later for amazing free experiences!"}
                            {category === 'music' && "No music events found. Check back later for amazing concerts and festivals!"}
                            {category === 'business' && "No business events available. Check back later for networking opportunities!"}
                            {!['trending', 'weekend', 'free', 'music', 'business'].includes(category || '') && "No events found in this category. Check back later for amazing experiences!"}
                        </p>
                        <div className="empty-state-actions">
                            <Link to="/events" className="hero-btn secondary">
                                <i className="fas fa-home"></i>
                                Back to Events
                            </Link>
                        </div>
                    </div>
                )}
            </div>

            
        </div>
    );
};

export default EventCategory;
